/**
 * 003 — Lastschriften ohne Beleg (US4, FR-030..FR-033, FR-UI-002): recurring
 * outgoing movements without a confirmed document, grouped by counterparty,
 * plus one-off debits without a receipt. Groups and single movements can be
 * marked "kein Beleg erforderlich" with a reason (audited, reversible) and are
 * then only visible under "Ausgeblendete zeigen". Both GF and Stbr may dismiss.
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, EmptyState, GlassPanel, Input, LoadingSpinner, StatusBadge } from '../ui';
import { ApiError } from '../../services/api';
import { bankApi, bankKeys, day, ibanLabel, money, type Dismissed, type RecurringReport } from '../../lib/bank-api';
import { errorText } from './ReviewEditor';
import type { BankTabProps } from './types';

type Group = RecurringReport['groups'][number];
type OneOff = RecurringReport['oneOff'][number];
type Supplier = Group['supplier'];
type DismissBody = { counterpartyKey?: string; transactionId?: string; reason: string };

const INTERVAL_LABELS: Record<Group['interval'], string> = {
  monthly: 'monatlich',
  quarterly: 'quartalsweise',
  yearly: 'jährlich',
};

const linkClass = 'underline decoration-white/30 underline-offset-2 hover:decoration-white text-white/90';

function SupplierHint({ supplier, request }: { supplier: Supplier; request: string }) {
  return supplier ? (
    <p className="text-sm" data-testid="recurring-supplier-hint">
      Lieferant:{' '}
      <Link to="/suppliers/$id" params={{ id: supplier.id }} className={linkClass}>
        {supplier.displayName}
      </Link>{' '}
      — {request}
    </p>
  ) : (
    <p className="text-sm text-white/60">Kein Lieferant im System erkannt.</p>
  );
}

function DismissedNote({ dismissed }: { dismissed: NonNullable<Dismissed> }) {
  return (
    <p className="min-w-0 max-w-full text-sm text-white/80 break-words [overflow-wrap:anywhere]">
      Ausgeblendet: {dismissed.reason}
      <span className="block text-xs text-white/50">
        kein Beleg erforderlich · am {day(dismissed.at)} · von {dismissed.by}
      </span>
    </p>
  );
}

function DismissForm({
  label,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  label: string;
  pending: boolean;
  error: string;
  onSubmit: (reason: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState('');
  const valid = reason.trim().length >= 3;
  return (
    <form
      className="mt-3 space-y-2 rounded border border-white/10 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && !pending) onSubmit(reason.trim());
      }}
    >
      <p className="text-sm text-white/70">
        Kein Beleg erforderlich für {label}? Die Begründung wird mit Nutzer und Zeitpunkt protokolliert; die Entscheidung
        lässt sich jederzeit zurücknehmen.
      </p>
      <Input
        name="reason"
        aria-label="Begründung"
        placeholder="Begründung, z. B. privat oder durch Kontoauszug belegt (mind. 3 Zeichen)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        autoFocus
      />
      {error && (
        <p role="alert" className="text-sm text-red-300">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" disabled={!valid || pending} loading={pending}>
          Ausblenden
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Abbrechen
        </Button>
      </div>
    </form>
  );
}

export function RecurringTab(props: BankTabProps) {
  const [includeDismissed, setIncludeDismissed] = useState(false);
  /** Only one dismiss form is open at a time: `g:<group key>` or `t:<transaction id>`. */
  const [openForm, setOpenForm] = useState<string | null>(null);
  const [formError, setFormError] = useState('');
  const [undismissError, setUndismissError] = useState<{ id: string; message: string } | null>(null);
  const client = useQueryClient();
  const params = { from: props.from, to: props.to, includeDismissed };
  const query = useQuery({
    queryKey: bankKeys.recurring(props.tenantId, props.from, props.to, includeDismissed),
    queryFn: () => bankApi.recurring(params),
    enabled: Boolean(props.tenantId),
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 1,
  });
  const dismiss = useMutation({
    mutationFn: (body: DismissBody) => bankApi.dismiss(body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: bankKeys.all });
      setOpenForm(null);
      setFormError('');
      props.onNotice('Ausgeblendet: kein Beleg erforderlich. Begründung protokolliert.');
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 409) {
        void client.invalidateQueries({ queryKey: bankKeys.all });
        setOpenForm(null);
        setFormError('');
        props.onNotice(`${errorText(e)} — die Ansicht wurde aktualisiert.`);
      } else setFormError(errorText(e));
    },
  });
  const undismiss = useMutation({
    mutationFn: (id: string) => bankApi.undismiss(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: bankKeys.all });
      setUndismissError(null);
      props.onNotice('Wieder eingeblendet. Die Buchungen stehen wieder in der Liste.');
    },
    onError: (e, id) => {
      if (e instanceof ApiError && e.status === 409) {
        void client.invalidateQueries({ queryKey: bankKeys.all });
        setUndismissError(null);
        props.onNotice(`${errorText(e)} — die Ansicht wurde aktualisiert.`);
      } else setUndismissError({ id, message: errorText(e) });
    },
  });

  const openFor = (key: string) => {
    setFormError('');
    setOpenForm(key);
  };

  const actions = (formKey: string, label: string, dismissed: Dismissed, body: Omit<DismissBody, 'reason'>, testid: string) =>
    dismissed ? (
      <div className="mt-3 flex flex-wrap items-end justify-between gap-2">
        <DismissedNote dismissed={dismissed} />
        <div>
          <Button
            variant="secondary"
            size="sm"
            disabled={undismiss.isPending}
            onClick={() => undismiss.mutate(dismissed.id)}
          >
            Wieder einblenden
          </Button>
          {undismissError?.id === dismissed.id && (
            <p role="alert" className="mt-1 text-sm text-red-300">
              {undismissError.message}
            </p>
          )}
        </div>
      </div>
    ) : openForm === formKey ? (
      <DismissForm
        label={label}
        pending={dismiss.isPending}
        error={formError}
        onSubmit={(reason) => dismiss.mutate({ ...body, reason })}
        onCancel={() => setOpenForm(null)}
      />
    ) : (
      <div className="mt-3">
        <Button variant="secondary" size="sm" data-testid={testid} onClick={() => openFor(formKey)}>
          Kein Beleg erforderlich
        </Button>
      </div>
    );

  const data = query.data;
  const period = data?.window ?? { from: props.from, to: props.to };

  const groupCard = (g: Group) => {
    const name = g.key === 'fee:bank' ? 'Bankentgelte (Kontoabschluss)' : (g.counterpartyName ?? 'Unbekannte Gegenpartei');
    const interval = INTERVAL_LABELS[g.interval];
    const pending = g.pendingSuggestionCount ?? 0;
    return (
      <article
        key={g.key}
        data-testid="recurring-group"
        className={`rounded-lg border border-white/10 p-4 ${g.dismissed ? 'opacity-70' : ''}`}
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-medium break-words">{name}</h3>
            <p className="text-sm text-white/60 break-all">IBAN {ibanLabel(g.iban)}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <StatusBadge tone="info" size="md">
              {interval} wiederkehrend
            </StatusBadge>
            <StatusBadge tone={g.coveredCount > 0 ? 'success' : 'muted'} size="md">
              {g.coveredCount} mit Beleg
            </StatusBadge>
            {pending > 0 && (
              <StatusBadge tone="warning" size="md" data-testid="recurring-pending">
                {pending} mit Belegvorschlag
              </StatusBadge>
            )}
            {g.dismissed && (
              <StatusBadge tone="muted" size="md">
                Ausgeblendet
              </StatusBadge>
            )}
          </div>
        </div>
        {pending > 0 && (
          <p className="mt-2 text-sm text-amber-200">
            Für {pending} dieser Abbuchungen liegt bereits ein passender Beleg vor — bitte im Tab „Bankbuchungen“
            bestätigen statt beim Lieferanten anzufordern.
          </p>
        )}
        <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-white/50">Typischer Betrag</dt>
            <dd>
              {/* typicalCents is the median ABSOLUTE amount; these are debits */}
              {money(-Math.abs(g.typicalCents))} · ohne Beleg ({g.occurrences.length}×)
            </dd>
          </div>
          <div>
            <dt className="text-white/50">Erste / letzte Abbuchung</dt>
            <dd>
              {day(g.firstDate)} – {day(g.lastDate)}
            </dd>
          </div>
          <div>
            <dt className="text-white/50">Summe im Zeitraum</dt>
            <dd>{money(g.sumCents)}</dd>
          </div>
          <div>
            <dt className="text-white/50">Abgedeckt</dt>
            <dd>{g.coveredCount} mit Beleg</dd>
          </div>
        </dl>
        <div className="mt-3 space-y-1">
          <SupplierHint supplier={g.supplier} request="Dauerrechnung/Abschlagsplan beim Lieferanten anfordern" />
          <p className="text-sm">
            <Link to="/expenses/upload" className={linkClass}>
              Beleg hochladen
            </Link>
          </p>
        </div>
        <details className="mt-3">
          <summary className="cursor-pointer text-sm text-white/70">
            Abbuchungen ohne Beleg anzeigen ({g.occurrences.length})
          </summary>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white/50">
                  <th className="text-left font-normal">Datum</th>
                  <th className="text-right font-normal">Betrag</th>
                  <th className="pl-3 text-left font-normal">Verwendungszweck</th>
                </tr>
              </thead>
              <tbody>
                {g.occurrences.map((o) => (
                  <tr key={o.transactionId} className="border-t border-white/5 align-top">
                    <td className="whitespace-nowrap py-1">{day(o.bookingDate)}</td>
                    <td className="whitespace-nowrap py-1 text-right">{money(o.amountCents)}</td>
                    <td className="min-w-[12rem] py-1 pl-3 break-words">{o.reference || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
        {actions(`g:${g.key}`, `alle Abbuchungen von ${name}`, g.dismissed, { counterpartyKey: g.key }, 'recurring-dismiss')}
      </article>
    );
  };

  const oneOffItem = (o: OneOff) => {
    const name =
      o.counterparty.key === 'fee:bank'
        ? 'Bankentgelte (Kontoabschluss)'
        : (o.counterparty.name ?? (o.counterparty.reference || 'Unbekannte Gegenpartei'));
    return (
      <li
        key={o.transactionId}
        data-testid="recurring-oneoff"
        className={`rounded-lg border border-white/10 p-3 ${o.dismissed ? 'opacity-70' : ''}`}
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-medium break-words">{name}</p>
            <p className="text-sm text-white/60 break-all">IBAN {ibanLabel(o.counterparty.iban)}</p>
          </div>
          <div className="text-right text-sm">
            <div className="font-medium">{money(o.amountCents)}</div>
            <div className="text-white/60">{day(o.bookingDate)}</div>
          </div>
        </div>
        {o.counterparty.name && o.counterparty.reference && (
          <p className="mt-1 text-sm text-white/60 break-words">{o.counterparty.reference}</p>
        )}
        {o.pendingSuggestion && (
          <p className="mt-1 text-sm text-amber-200" data-testid="recurring-oneoff-pending">
            Beleg vorgeschlagen — im Tab „Bankbuchungen“ bestätigen.
          </p>
        )}
        <div className="mt-2 space-y-1">
          <SupplierHint supplier={o.supplier} request="Rechnung beim Lieferanten anfordern" />
          <p className="text-sm">
            <Link to="/expenses/upload" className={linkClass}>
              Beleg hochladen
            </Link>
          </p>
        </div>
        {actions(
          `t:${o.transactionId}`,
          `die Abbuchung vom ${day(o.bookingDate)}`,
          o.dismissed,
          { transactionId: o.transactionId },
          'recurring-oneoff-dismiss'
        )}
      </li>
    );
  };

  return (
    <div className="space-y-6">
      <GlassPanel>
        <h2 className="text-lg font-semibold">Lastschriften ohne Beleg</h2>
        <p className="my-2 text-sm text-white/70">
          Wiederkehrende Abbuchungen ohne bestätigten Beleg sind Kandidaten für fehlende Dauerrechnungen oder
          Abschlagspläne — ohne Beleg fehlt der Vorsteuerabzug. Bestätigte Steuerzahlungen, Gesellschafter-Flüsse,
          Umbuchungen, Bankentgelte, Auszahlungen, Privatbuchungen und Rücklastschrift-Paare sind nicht enthalten.
        </p>
        <p className="text-sm text-white/70" data-testid="recurring-window">
          Zeitraum: {day(period.from)} – {day(period.to)} (Buchungstag)
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-4">
          <label className="inline-flex min-h-11 items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-white/10 bg-surface"
              checked={includeDismissed}
              onChange={(e) => {
                setIncludeDismissed(e.target.checked);
                setOpenForm(null);
              }}
            />
            Ausgeblendete zeigen
          </label>
          <a
            href={bankApi.recurringCsvUrl(params)}
            className="inline-flex min-h-11 items-center rounded-lg border border-white/10 px-4 text-sm text-white/80 hover:border-white/20 hover:bg-white/5"
          >
            CSV exportieren
          </a>
        </div>
      </GlassPanel>
      {query.isLoading ? (
        <LoadingSpinner />
      ) : query.error ? (
        <p role="alert" className="rounded border border-red-400/40 p-3">
          {errorText(query.error)}
        </p>
      ) : !data ? null : data.groups.length === 0 && data.oneOff.length === 0 ? (
        <EmptyState title="Keine Lastschriften ohne Beleg im Zeitraum." />
      ) : (
        <>
          <GlassPanel>
            <h2 className="text-lg font-semibold">Wiederkehrend ohne Beleg ({data.groups.length})</h2>
            {data.groups.length === 0 ? (
              <p className="mt-2 text-sm text-white/60">Keine wiederkehrenden Abbuchungen ohne Beleg.</p>
            ) : (
              <div className="mt-3 space-y-4">{data.groups.map(groupCard)}</div>
            )}
          </GlassPanel>
          <GlassPanel>
            <h2 className="text-lg font-semibold">Einmalig ohne Beleg ({data.oneOff.length})</h2>
            {data.oneOff.length === 0 ? (
              <p className="mt-2 text-sm text-white/60">Keine einmaligen Abbuchungen ohne Beleg.</p>
            ) : (
              <ul className="mt-3 space-y-3">{data.oneOff.map(oneOffItem)}</ul>
            )}
          </GlassPanel>
        </>
      )}
    </div>
  );
}
